import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
} from '@angular/core';

export type UiCheckboxAppearance = 'default' | 'locked';

let uiCheckboxUid = 0;

/**
 * Átomo: checkbox custom con checkmark SVG, estado indeterminate,
 * badge obligatorio/opcional y mensaje de error controlable.
 */
@Component({
  selector: 'ui-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.scss',
  host: {
    class: 'ui-checkbox-host',
    '[class.ui-checkbox-host--box-only]': 'boxOnly()',
  },
})
export class UiCheckboxComponent {
  readonly checked = model(false);
  readonly indeterminate = input(false);
  readonly label = input('');
  readonly description = input<string | undefined>(undefined);

  readonly required = input(false);
  readonly showOptionalHint = input(false);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  /** Deshabilitado visualmente como marcado: sin opacidad tenue. */
  readonly appearance = input<UiCheckboxAppearance>('default');
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly name = input<string | undefined>(undefined);
  readonly value = input<string | undefined>(undefined);
  readonly fieldId = input<string | undefined>(undefined);

  readonly checkedChange = output<boolean>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly generatedId = `ui-checkbox-${++uiCheckboxUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);

  protected readonly ariaDescribedBy = computed(() => {
    const parts: string[] = [];
    if (this.description()) parts.push(`${this.inputId()}-desc`);
    if (this.message() && this.invalid()) parts.push(this.messageId());
    return parts.length ? parts.join(' ') : null;
  });

  protected readonly isLocked = computed(() => this.appearance() === 'locked');

  protected readonly isInteractionBlocked = computed(
    () => this.disabled() || this.isLocked(),
  );

  protected readonly boxOnly = computed(
    () => !this.label() && !this.description(),
  );

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected onChange(event: Event): void {
    if (this.isInteractionBlocked()) return;
    const v = (event.target as HTMLInputElement).checked;
    this.checked.set(v);
    this.checkedChange.emit(v);
  }

  protected onBlur(event: FocusEvent): void {
    this.blurred.emit(event);
  }

  protected onFocus(event: FocusEvent): void {
    this.focused.emit(event);
  }
}
